import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import ModeCard from "../components/ModeCard";
import { StandardIcon, RankedIcon, DailyIcon } from "../components/modeIcons";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import RankedModal from "../components/RankedModal";
import TierBadge from "../components/TierBadge";
import { getTier } from "../sport/tiers";
import SocialLinks from "../components/SocialLinks";
import SPORT from "../sport/config";

export default function HubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bestStandard, setBestStandard] = useState(null);
  const [rankedRating, setRankedRating] = useState(null);
  const [rankedOpen, setRankedOpen] = useState(false);
  const [rankedRecord, setRankedRecord] = useState(null);
  //const [rankedRank, setRankedRank] = useState(null);

  useEffect(() => {
    if (!user) {
      setBestStandard(null);
      setRankedRating(null);
      setRankedRecord(null);
      //setRankedRank(null);
      return;
    }
    let cancelled = false;

    supabase
      .from("runs")
      .select("*")
      .eq("user_id", user.id)
      .eq("mode", "standard")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("Failed to load best run:", error);
          return;
        }
        const best = (data || []).reduce((acc, run) => {
          const pts = run.wins * 3 + run.draws;
          const accPts = acc ? acc.wins * 3 + acc.draws : -1;
          return pts > accPts ? run : acc;
        }, null);
        setBestStandard(best);
      });

    supabase
      .from("leaderboard_entries")
      .select("glicko_rating")
      .eq("user_id", user.id)
      .eq("mode", "ranked")
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setRankedRating(data?.glicko_rating ?? null);
      });

    supabase
      .from("matches")
      .select("player1, player2, outcome")
      .eq("status", "complete")
      .then(({ data }) => {
        if (cancelled) return;
        let w = 0,
          d = 0,
          l = 0;
        (data || []).forEach((m) => {
          if (!m.outcome) return;
          if (m.outcome === "tie") d++;
          else {
            const won =
              (m.outcome === "p1" && m.player1 === user.id) ||
              (m.outcome === "p2" && m.player2 === user.id);
            won ? w++ : l++;
          }
        });
        setRankedRecord({ w, d, l });
      });

    // supabase
    //   .rpc("get_leaderboard_rank", { p_mode: "ranked", p_user: user.id })
    //   .then(({ data }) => {
    //     if (!cancelled) setRankedRank(data ?? null);
    //   });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const rankedTier = getTier(rankedRating);

  return (
    <div className="h-screen flex flex-col">
      <Header />

      <main className="flex-1 overflow-y-auto scrollbar-fancy">
        <div className="w-full max-w-5xl mx-auto px-5 py-8 md:py-14">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-100 mb-2">
              Choose your mode
            </h1>
            {/* <p className="text-sm md:text-base text-zinc-500">
              Build your XI. Project your season. Compete on the global
              leaderboard.
            </p> */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <ModeCard
              icon={StandardIcon}
              title="Standard"
              description={SPORT.copy.standardDesc}
              best={bestStandard}
              onClick={() => navigate("/standard")}
            />
            <ModeCard
              icon={RankedIcon}
              title="Ranked"
              description={SPORT.copy.rankedDesc}
              footerLabel="Rating"
              best={rankedRating != null ? Math.round(rankedRating) : null}
              bestUnit="SR"
              valueColor={rankedTier?.text || "text-emerald-400"}
              badge={
                rankedRating != null ? (
                  <TierBadge sr={rankedRating} />
                ) : undefined
              }
              footerExtra={
                rankedRecord &&
                rankedRecord.w + rankedRecord.d + rankedRecord.l > 0 ? (
                  <span className="text-xs tabular-nums">
                    <span className="text-emerald-400 font-semibold">
                      {rankedRecord.w}
                    </span>
                    <span className="text-zinc-600">W</span>
                    {rankedRecord.d > 0 && (
                      <>
                        <span className="text-zinc-700 mx-1">·</span>
                        <span className="text-zinc-400 font-semibold">
                          {rankedRecord.d}
                        </span>
                        <span className="text-zinc-600">D</span>
                      </>
                    )}
                    <span className="text-zinc-700 mx-1">·</span>
                    <span className="text-red-400 font-semibold">
                      {rankedRecord.l}
                    </span>
                    <span className="text-zinc-600">L</span>
                  </span>
                ) : null
              }
              onClick={() => setRankedOpen(true)}
            />
            <ModeCard
              icon={DailyIcon}
              title="Daily Challenge"
              description={SPORT.copy.dailyDesc}
              best="—"
              comingSoon
            />
          </div>

          {/* Leaderboards entry */}
          <button
            onClick={() => navigate("/leaderboard")}
            className="mt-4 md:mt-6 w-full flex items-center gap-3 rounded-2xl
                       bg-zinc-900/50 border border-zinc-800 px-5 py-4
                       hover:border-emerald-500/50 hover:bg-zinc-900/70 transition group"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 grid place-items-center shrink-0">
              <RankedIcon className="w-5 h-5" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-sm font-bold text-zinc-100">
                Leaderboards
              </div>
              <div className="text-xs text-zinc-500 truncate">
                See where you rank across modes
              </div>
            </div>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-5 h-5 text-zinc-600 ml-auto shrink-0 group-hover:text-zinc-400 transition"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          {/* Social */}
          <div className="mt-8 md:mt-10 flex flex-col items-center gap-2.5">
            <SocialLinks />
            <span className="text-[10px] uppercase tracking-widest text-zinc-600">
              {SPORT.socialHandle}
            </span>
          </div>
        </div>
      </main>

      <RankedModal open={rankedOpen} onClose={() => setRankedOpen(false)} />
    </div>
  );
}
